'use client';

import { ChevronDown, Expand } from 'lucide-react';
import type { Media } from '@payload-types';
import { cn } from '../../../utilities/classname';
import { MediaElement } from '../media-element';
import type { SubjectsById } from '../subjects-by-id';

/**
 * The three surfaces this control is laid over. Each decides what cannot be
 * derived from the theme: the frame, how the photos fit it, the divider, the
 * handle (or none), and where and how the two pills sit.
 */
export type PortfolioComparisonVariant = 'showcase' | 'filmstrip' | 'lightbox';

/** The default divider position, in percent. Dead centre, every style. */
export const PORTFOLIO_COMPARISON_DEFAULT_PCT = 50;

type VariantStyle = {
  frame: string;
  fit: string;
  divider: string;
  handle: { className: string; chevron: string } | null;
  beforePill: string;
  afterPill: string;
};

const AFTER_PILL_COLOURS = 'bg-primary text-primary-content';

const VARIANT_STYLES: Record<PortfolioComparisonVariant, VariantStyle> = {
  // On the dark band the divider is drawn from the band's own foreground and
  // the `before` pill is a translucent lift of it — a `base-content` pill would
  // be near-black on near-black.
  showcase: {
    frame: 'rounded-box bg-base-300',
    fit: 'object-cover',
    divider: 'bg-neutral-content/90',
    handle: { className: 'size-11 top-1/3', chevron: 'size-5' },
    beforePill: 'top-4 left-4 bg-neutral-content/15 text-neutral-content/85',
    afterPill: `top-4 right-4 ${AFTER_PILL_COLOURS}`,
  },
  filmstrip: {
    frame: 'rounded-box bg-base-300',
    fit: 'object-cover',
    divider: 'bg-base-100 ring-1 ring-base-content/10',
    handle: { className: 'size-10 top-1/2', chevron: 'size-4' },
    beforePill: 'top-4 left-4 bg-base-content/75 text-base-100',
    afterPill: `top-4 right-4 ${AFTER_PILL_COLOURS}`,
  },
  // The lightbox stage: the WHOLE photo is shown (`object-contain` on black),
  // so nothing is cropped from a job the visitor asked to see enlarged. No
  // handle — the divider line is the affordance, and a centred handle would sit
  // under the lightbox's own prev/next arrows. The pills move to the bottom
  // corners because the top-right corner holds the close button.
  lightbox: {
    frame: 'bg-black',
    fit: 'object-contain',
    divider: 'bg-white',
    handle: null,
    beforePill: 'bottom-4 left-4 bg-base-100/90 text-base-content',
    afterPill: `bottom-4 right-4 ${AFTER_PILL_COLOURS}`,
  },
};

const PILL_BASE = 'absolute z-20 font-mono uppercase tracking-widest rounded-selector px-2.5 py-1 pointer-events-none text-xs';

/**
 * A draggable before/after comparison of one portfolio item.
 *
 * CONTROLLED, deliberately. The divider position belongs to the parent style,
 * not to this component: the filmstrip mounts a SECOND copy of this for the
 * item it zooms, and the two have to agree — a divider dragged in the modal
 * belongs where the visitor left it on the tile behind it, which state held
 * here could not reach. Every style therefore keeps every position in one
 * record keyed by item id — see `use-portfolio-sliders.ts`.
 *
 * The control is a real `<input type="range">` stretched over the whole figure
 * at `opacity-0`. That is what buys keyboard stepping, touch dragging and a
 * screen-reader-announced value for free; a `pointermove` handler on the
 * wrapper would have to reimplement all three and would still not appear in the
 * tab order. Its transparency is why every label above it is
 * `pointer-events-none`: a pill that swallowed the pointer would create dead
 * zones in the drag surface.
 *
 * NO TRANSITION on the divider, the handle or the clip. Dragging has to track
 * the pointer exactly — even 100ms of easing reads as the image lagging behind
 * the finger.
 *
 * DELIBERATELY NOT daisyUI's `diff` (which `image-diff-block.tsx` does use).
 * `diff` draws the same two clipped layers, but its `diff-resizer` is a native
 * CSS `resize` box: its position lives in the browser's layout state, not in
 * React's, so it cannot be read back to persist per item, cannot be restored
 * when the showcase swaps items, and cannot be placed at the design's handle
 * offsets. Every one of those is a requirement here, so the two layers are
 * drawn directly and the position stays a controlled number.
 *
 * DEGRADES TO A PLAIN PHOTO WITHOUT LAYOUT SHIFT. When the item has no
 * comparison pair the wrapper, its aspect ratio and its frame are unchanged;
 * only the second layer, the divider, the pills and the input are dropped.
 * Every style therefore lays out identically whether or not an editor
 * uploaded a pair.
 */
export const PortfolioComparison: React.FC<{
  variant: PortfolioComparisonVariant;
  /** The comparison pair. Both null renders {@link image} alone. */
  before: Media | null;
  after: Media | null;
  /** The single-photo fallback, and the base layer of the comparison. */
  image: Media;
  pct: number;
  onPct: (pct: number) => void;
  /** Names the item in the control's accessible label. */
  title: string;
  aspect: string;
  sizes: string;
  /**
   * Opens the caller's zoom modal. Renders a button when supplied, and nothing
   * at all when not — the modal belongs to the caller, which is the thing that
   * holds the item's text.
   */
  onZoom?: () => void;
  /** Show the stored blur while the photos load; the caller decides which items earn it. */
  placeholder?: boolean;
  /** The photos' Subject attributes by Media id, from the server caller (`portfolioSubjects`). */
  subjects?: SubjectsById;
}> = ({ variant, before, after, image, pct, onPct, title, aspect, sizes, onZoom, placeholder, subjects }) => {
  const styles = VARIANT_STYLES[variant];
  const wrapper = cn('relative overflow-hidden', styles.frame, aspect);
  const fit = cn(styles.fit, 'object-center');

  /*
   * A DEDICATED TARGET, not the photo itself, and the range input below is why.
   * That input is stretched over the whole figure at `opacity-0` to buy the drag
   * — so it swallows every pointer event on the photo, and a bare click on a
   * range input also JUMPS the thumb. Wiring zoom to a click on the photo would
   * therefore both open the modal and move the divider out from under the
   * visitor's finger. An explicit button avoids that, and it is the only form
   * that reaches the keyboard and the accessibility tree at all.
   *
   * `z-40` puts it above that input (`z-30`); bottom-right is the one corner the
   * pills (top) and the handle (centre) leave free.
   */
  const zoomButton = onZoom === undefined ? null : (
    <button
      type="button"
      onClick={onZoom}
      aria-label={`Enlarge photo: ${title}`}
      aria-haspopup="dialog"
      className="btn btn-circle btn-sm absolute bottom-4 right-4 z-40"
    >
      <Expand className="size-4" aria-hidden />
    </button>
  );

  /*
   * NO `priority`, on either layer, in any style — deliberately, and this is
   * the one prop worth explaining by its absence.
   *
   * `MediaElement.priority` injects a `<link rel="preload">` and sets
   * `fetchPriority="high"`, and its own docblock caps that at ONE image per
   * page. On every page that renders a portfolio section, that image is the
   * hero, which is the LCP — and the section is below the fold at every
   * viewport on all of them (a market page puts seven bands above it, the
   * service landing page three). Preloading a photo here would compete with the
   * real LCP for bandwidth and dilute the priority hint that exists to protect
   * it. Lazy is both the default and the correct answer here.
   *
   * It is also what keeps the showcase's hidden slides off the network. That
   * style renders every item and hides all but one, so a page carries a photo
   * per job; a lazy image inside a `display: none` slide never intersects the
   * viewport, so it is not fetched until its slide is shown.
   */
  if (before === null || after === null) {
    return (
      <div className={wrapper}>
        <MediaElement data={image} fill sizes={sizes} placeholder={placeholder} className={fit} subject={subjects?.[image.id]} />
        {zoomButton}
      </div>
    );
  }

  return (
    <div className={wrapper}>
      <MediaElement data={after} fill sizes={sizes} placeholder={placeholder} className={fit} subject={subjects?.[after.id]} />

      {/*
        The BEFORE layer is clipped from the right, so the revealed width is the
        slider percentage. `inset()` insets from each edge in turn, which is why
        the right inset is the COMPLEMENT of the position rather than the
        position itself — the classic off-by-inversion in this control.
      */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}>
        <MediaElement data={before} fill sizes={sizes} placeholder={placeholder} className={fit} subject={subjects?.[before.id]} />
      </div>

      <div
        aria-hidden
        className={cn('absolute top-0 bottom-0 z-10 w-0.5 -translate-x-1/2', styles.divider)}
        style={{ left: `${pct}%` }}
      />

      {styles.handle === null ? null : (
        <div
          aria-hidden
          className={cn(
            'absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-base-100',
            'flex items-center justify-center drop-shadow-md drop-shadow-base-content',
            styles.handle.className,
          )}
          style={{ left: `${pct}%` }}
        >
          <ChevronDown className={cn('-rotate-90 text-base-content/60', styles.handle.chevron)} />
        </div>
      )}

      <span className={cn(PILL_BASE, styles.beforePill)}>before</span>
      <span className={cn(PILL_BASE, styles.afterPill)}>after</span>

      <input
        type="range"
        min={0}
        max={100}
        value={pct}
        onChange={(event) => onPct(Number(event.target.value))}
        aria-label={`Reveal before and after: ${title}`}
        aria-valuetext={`${pct}% before, ${100 - pct}% after`}
        className="peer absolute inset-0 z-30 m-0 h-full w-full cursor-ew-resize opacity-0"
      />
      {/* The transparent native range still needs a visible keyboard focus indicator.
          White and black outlines stay visible against either photo, without JavaScript. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-1 z-30 rounded-sm peer-focus-visible:ring-2 peer-focus-visible:ring-white peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-black"
      />

      {zoomButton}
    </div>
  );
};
